import { apiClient } from './client';
import type {
  DirectoryMonitor,
  MonitorListItem,
  MonitorStats,
  MonitorScan,
  CreateMonitorRequest,
  UpdateMonitorRequest,
  CreateMonitorResponse,
  UpdateMonitorResponse,
  DeleteMonitorResponse,
  TriggerScanRequest,
  TriggerScanResponse,
  ToggleMonitorRequest,
  DirectoryValidationResult,
  ListSubdirectoriesResponse,
  MonitorListFilters,
  PaginationParams,
  PaginatedResponse,
} from '@/features/monitors/types';

/**
 * Directory Monitor API endpoints
 *
 * Provides methods for managing directory monitors in the data lakehouse:
 * - List, get, create, update, delete monitors
 * - Get monitor statistics and scan history
 * - Trigger manual scans
 * - Pause/resume monitors
 * - Validate directory paths
 */
export const monitorEndpoints = {
  /**
   * List all directory monitors with optional filtering and pagination
   *
   * @param params - Optional query parameters for filtering and pagination
   * @param params.status - Filter by monitor status (active, paused, disabled, error)
   * @param params.dataset_id - Filter by target dataset ID
   * @param params.monitor_type - Filter by monitor type (scheduled, continuous, event-driven)
   * @param params.auto_ingest - Filter by auto-ingest flag
   * @param params.search - Search by name or directory path
   * @param params.limit - Number of items per page
   * @param params.offset - Number of items to skip
   * @param params.sort_by - Field to sort by
   * @param params.sort_order - Sort direction (asc or desc)
   * @returns Promise resolving to paginated monitor list
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.list({
   *   status: 'active',
   *   limit: 20,
   *   sort_by: 'last_scan_at',
   *   sort_order: 'desc'
   * });
   * console.log(response.data.items); // MonitorListItem[]
   * ```
   */
  list: (params?: MonitorListFilters & PaginationParams) =>
    apiClient.get<PaginatedResponse<MonitorListItem>>('/monitors', { params }),

  /**
   * Get detailed information for a specific directory monitor
   *
   * @param id - Monitor UUID
   * @returns Promise resolving to full monitor details including relationships
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.get('550e8400-e29b-41d4-a716-446655440000');
   * console.log(response.data.file_patterns); // FilePattern
   * console.log(response.data.dataset); // Dataset details
   * ```
   */
  get: (id: string) => apiClient.get<DirectoryMonitor>(`/monitors/${id}`),

  /**
   * Create a new directory monitor
   *
   * @param data - Monitor creation payload
   * @param data.name - Unique monitor name
   * @param data.directory_path - Absolute path to monitor
   * @param data.dataset_id - Target dataset UUID for ingested files
   * @param data.description - Optional description
   * @param data.file_patterns - File matching patterns (include/exclude)
   * @param data.scan_frequency - How often to scan (daily, hourly, every-15-min, continuous)
   * @param data.monitor_type - Trigger type (scheduled, continuous, event-driven)
   * @param data.auto_ingest - Whether to automatically trigger ingestion
   * @param data.default_schema_id - Optional default schema UUID
   * @returns Promise resolving to created monitor
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.create({
   *   name: 'sales_data_monitor',
   *   directory_path: '/data/incoming/sales',
   *   dataset_id: '550e8400-e29b-41d4-a716-446655440000',
   *   file_patterns: {
   *     include_patterns: ['*.parquet', '*.csv'],
   *     exclude_patterns: ['*_temp.*'],
   *     recursive: true
   *   },
   *   scan_frequency: 'hourly',
   *   auto_ingest: true,
   *   description: 'Monitor incoming sales data files'
   * });
   * ```
   */
  create: (data: CreateMonitorRequest) =>
    apiClient.post<CreateMonitorResponse>('/monitors', data),

  /**
   * Update an existing directory monitor
   *
   * All fields are optional to allow partial updates.
   *
   * @param id - Monitor UUID
   * @param data - Partial monitor update payload
   * @returns Promise resolving to updated monitor
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.update('550e8400-e29b-41d4-a716-446655440000', {
   *   scan_frequency: 'daily',
   *   description: 'Updated to scan daily instead of hourly'
   * });
   * ```
   */
  update: (id: string, data: UpdateMonitorRequest) =>
    apiClient.put<UpdateMonitorResponse>(`/monitors/${id}`, data),

  /**
   * Delete a directory monitor
   *
   * WARNING: This will stop monitoring the directory and remove scan history.
   * Active scans will be cancelled. This operation cannot be undone.
   *
   * @param id - Monitor UUID
   * @returns Promise resolving when deletion is complete
   *
   * @example
   * ```ts
   * await monitorEndpoints.delete('550e8400-e29b-41d4-a716-446655440000');
   * ```
   */
  delete: (id: string) =>
    apiClient.delete<DeleteMonitorResponse>(`/monitors/${id}`),

  /**
   * Get aggregated statistics for a monitor
   *
   * Provides comprehensive metrics including file counts, success rates,
   * scan durations, and data volume processed.
   *
   * @param id - Monitor UUID
   * @returns Promise resolving to monitor statistics
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.getStats('550e8400-e29b-41d4-a716-446655440000');
   * console.log(response.data.files_detected); // Total files discovered
   * console.log(response.data.success_rate); // Success percentage
   * console.log(response.data.average_scan_duration); // Avg scan time
   * ```
   */
  getStats: (id: string) =>
    apiClient.get<MonitorStats>(`/monitors/${id}/stats`),

  /**
   * Get scan history for a monitor
   *
   * Returns a list of scan executions with details about files discovered,
   * jobs created, and any errors encountered.
   *
   * @param id - Monitor UUID
   * @param params - Optional query parameters
   * @param params.limit - Maximum number of scans to return (default: 50)
   * @returns Promise resolving to scan history list
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.getScans('550e8400-e29b-41d4-a716-446655440000', {
   *   limit: 10
   * });
   * console.log(response.data); // MonitorScan[] - last 10 scans
   * ```
   */
  getScans: (id: string, params?: { limit?: number }) =>
    apiClient.get<MonitorScan[]>(`/monitors/${id}/scans`, { params }),

  /**
   * Trigger a manual directory scan
   *
   * Initiates an immediate scan of the monitored directory, bypassing
   * the regular schedule. Use the force flag to scan even if recently scanned.
   *
   * WARNING: This is a potentially long-running operation (up to 60 seconds).
   * The request will return immediately with a scan_id for tracking.
   *
   * @param id - Monitor UUID
   * @param data - Optional scan configuration
   * @param data.force - Force scan even if recently scanned
   * @returns Promise resolving to scan execution details
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.triggerScan('550e8400-e29b-41d4-a716-446655440000', {
   *   force: true
   * });
   * console.log(response.data.scan_id); // Track this scan
   * ```
   */
  triggerScan: (id: string, data?: TriggerScanRequest) =>
    apiClient.post<TriggerScanResponse>(`/monitors/${id}/scan`, data, {
      timeout: 60000, // 60 seconds for scan operations
    }),

  /**
   * Toggle monitor status between active and paused
   *
   * Use this to temporarily pause a monitor without deleting it.
   * Paused monitors will not execute scheduled scans but retain all configuration.
   *
   * @param id - Monitor UUID
   * @param data - Status toggle request
   * @param data.status - Desired status ('active' or 'paused')
   * @returns Promise resolving to updated monitor
   *
   * @example
   * ```ts
   * // Pause a monitor
   * const response = await monitorEndpoints.toggleStatus('550e8400-e29b-41d4-a716-446655440000', {
   *   monitor_id: '550e8400-e29b-41d4-a716-446655440000',
   *   status: 'paused'
   * });
   * ```
   */
  toggleStatus: (id: string, data: ToggleMonitorRequest) =>
    apiClient.post<UpdateMonitorResponse>(`/monitors/${id}/toggle`, data),

  /**
   * Validate a directory path for monitoring
   *
   * Checks if a directory path exists, is accessible, and provides metadata
   * about the directory contents. Use this before creating a monitor.
   *
   * @param path - Absolute directory path to validate
   * @returns Promise resolving to validation results
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.validateDirectory('/data/incoming/sales');
   * if (!response.data.valid) {
   *   console.error('Path validation failed:', response.data.errors);
   * } else {
   *   console.log('Matching files:', response.data.metadata?.matching_files);
   * }
   * ```
   */
  validateDirectory: (path: string) =>
    apiClient.post<DirectoryValidationResult>('/monitors/validate-directory', {
      directory_path: path,
    }),

  /**
   * List subdirectories in a directory path
   *
   * Returns immediate subdirectories with optional metadata including
   * file counts and sizes. Used for partition mapping in monitor creation.
   *
   * @param path - Absolute directory path to list subdirectories from
   * @param depth - Depth of subdirectories to scan (1=immediate children, 2=grandchildren, etc.)
   * @returns Promise resolving to list of subdirectories
   *
   * @example
   * ```ts
   * const response = await monitorEndpoints.listSubdirectories('/data/incoming', 2);
   * console.log(response.data.subdirectories); // SubdirectoryInfo[] with level property
   * console.log(response.data.total); // Total subdirectory count
   * ```
   */
  listSubdirectories: (path: string, depth: number = 1) =>
    apiClient.post<ListSubdirectoriesResponse>('/monitors/list-subdirectories', {
      directory_path: path,
      depth,
    }),
};
